import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Tooltip,
  Paper,
  Divider,
  Box,
  Grid as Grid,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import StarIcon from "@mui/icons-material/Star";
import { fullName } from "../../actprep-ui/utils/formatting";

function MasterSheetStat({ title, description, value, icon }) {
  return (
    <Tooltip title={description}>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          height: "100%",
          borderRadius: 2,
          backgroundColor: "background.primary",
          textAlign: "center",
          transition: "transform .2s",
          "&:hover": { transform: "scale(1.05)" },
        }}
      >
        {icon && <Box sx={{ mb: 1 }}>{icon}</Box>}
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
      </Paper>
    </Tooltip>
  );
}
function MaxIncreaseStat({ title, description, value, icon }) {
  const [increase, rem] = value.toString().split("(");
  if (!rem) {
    return (
      <MasterSheetStat title={title} description={description} value={value} />
    );
  }
  const [student, finalScore] = rem
    .replaceAll(")", "")
    .split(" - ")
    .map((item) => item.trim());

  return (
    <Tooltip title={description}>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          height: "100%",
          borderRadius: 2,
          backgroundColor: "background.primary",
          textAlign: "center",
          transition: "transform .2s",
          "&:hover": { transform: "scale(1.05)" },
        }}
      >
        {icon && <Box sx={{ mb: 1 }}>{icon}</Box>}
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {increase}
        </Typography>
        <Typography variant="body2" fontSize={".75rem"}>
          {student} - {finalScore}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
      </Paper>
    </Tooltip>
  );
}

function FinalCoachingScore({ score }) {
  return (
    <Paper
      elevation={4}
      sx={{
        p: 3,
        borderRadius: 2,
        textAlign: "center",
        background: "linear-gradient(45deg, #F44336, #EF5350)",
        color: "#fff",
      }}
    >
      <Typography variant="overline" sx={{ letterSpacing: 2 }}>
        FINAL COACHING SCORE
      </Typography>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={1}
        sx={{ mt: 1 }}
      >
        <StarIcon fontSize="large" />
        <Typography variant="h3" sx={{ fontWeight: "bold" }}>
          {score}%
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
        Overall team performance
      </Typography>
    </Paper>
  );
}

export default function MasterSheetTeamCard({ team }) {
  const coachUser = team.coach || team.assistant_coach?.user || {};
  const profilePic = coachUser.profile_pic || coachUser.profile?.profile_pic;
  const stats = team.stats || {};
  const teamTitle = team.team_name || team.label || team.role;
  const coachName = team.coach_name || fullName(coachUser) || team.name;
  const itgPercent = team.percent_in_the_game ?? stats.itg_percent ?? 0;
  const itgTotal = team.total_students_in_the_game ?? stats.itg_total ?? 0;
  return (
    <Card
      sx={{
        width: "100%",
        boxShadow: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            alt="Profile Picture"
            src={profilePic}
            sx={{ width: 56, height: 56 }}
          >
            {(coachUser.first_name?.[0] || "") +
              (coachUser.last_name?.[0] || "")}
          </Avatar>
        }
        title={
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {teamTitle}
          </Typography>
        }
        subheader={
          <Typography variant="subtitle2">
            {team.role ? `${team.role}: ` : "Coached by "}
            {coachName}
          </Typography>
        }
        sx={{ backgroundColor: "primary" }}
      />
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* General Stats Grid */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 4, sm: 3 }}>
              <MasterSheetStat
                title="ITG"
                value={`${itgPercent}% - ${itgTotal}`}
                description="Percentage of students in the game"
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 3 }}>
              <MasterSheetStat
                title="Check Ins"
                value={`${stats.check_ins ?? 0}%`}
                description="Percent of students who checked in every day"
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 3 }}>
              <MasterSheetStat
                title="Accuracy"
                value={`${stats.accuracy ?? 0}%`}
                description="Percent of students who accurately submitted their stats at least 5 days"
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 3 }}>
              <MasterSheetStat
                title="Hours"
                value={`${stats.hours ?? 0}%`}
                description="Percent of students who hit their hours"
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 3 }}>
              <MasterSheetStat
                title="Hours Volunteers"
                value={`${stats.hours_volunteers ?? 0}%`}
                description="Percent of hours completed by the hours volunteers"
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 3 }}>
              <MasterSheetStat
                title="Memorization"
                value={`${stats.memorization ?? 0}%`}
                description="Percent of students who scored at least an 80% on their quiz"
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 3 }}>
              <MasterSheetStat
                title="Score"
                value={`${stats.score ?? 0}%`}
                description="Percent of students who hit their goal score for the week"
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 3 }}>
              <MaxIncreaseStat
                title="Max Increase"
                value={stats.max_increase ?? 0}
                description="Max increase in the class subject"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Final Coaching Score Section */}
        <Box sx={{ mt: "auto" }}>
          <Divider sx={{ mb: 3 }} />
          <FinalCoachingScore score={stats.average ?? 0} />
        </Box>
      </CardContent>
    </Card>
  );
}
